// Pure HTML generator for the "Spotlight Webinar Invite" template.
//
// Emits a COMPLETE HTML document. crm-api stores it untouched (the layout is on
// the model's complete-HTML skip-sanitize list) and its mailer passes it through
// the `safe_campaign` allowlist, appending only the unsubscribe + open-tracking
// footer — so this file owns every pixel of the chrome.
//
// WHAT REACHES THE INBOX. `safe_campaign` removes `<style>`, every `class`,
// every HTML comment and CSS it doesn't know (`object-fit`) — with ONE
// exception: the exact Outlook ghost-table pair below (`MSO_GHOST_OPEN` /
// `MSO_GHOST_CLOSE`, byte-identical to crm-api's
// `CampaignSanitizerHelper::MSO_GHOST_*`). So the design is built only from
// things that survive AND that Outlook's Word engine understands:
//
//   * The card is fluid (`width:100%; max-width:600px`) for phones and every
//     browser-engine client, wrapped in the ghost table that pins it to 600px
//     in Outlook desktop, which ignores max-width. (A `width="600"` card with
//     `max-width:100%` does NOT work: Chrome ignores max-width on a fixed-width
//     table and the card runs off a phone.)
//   * Buttons are "padding on the CELL" buttons: Word ignores padding on an
//     `<a>` but honours it on a `<td bgcolor>`, so the button keeps its size in
//     Outlook 2007–2019 without VML (which the sanitizer would strip).
//   * Hairlines are 1px `border-top` on a cell — never an `<hr>` (Outlook
//     draws those thick and grey) or a background-image.
//   * Images carry explicit pixel `width`/`height` (Word ignores `width:auto`).
//   * Labels are upper-cased in the string, not with `text-transform`.
//
// The look: a neutral, editorial card — logo left with a quiet "LIVE WEBINAR"
// tag right, hairlines between sections, a tight headline, numbered topic rows
// separated by hairlines, the date in a neutral outlined panel, a soft grey
// footer. The hairlines, labels and panels are deliberately colour-less
// (`NEUTRAL_*`); only the palette's accent/button colours carry brand colour.

import { socialFooterLinksHtml } from "@crm-src/lib/email-social"
import {
  DEFAULT_SPOTLIGHT_COLORS,
  SPOTLIGHT_MAX_SPEAKERS,
  XOP_EMAIL_LOGO_URL,
  toAbsoluteUrl,
  type SpotlightColors,
  type SpotlightContent,
  type SpotlightSection,
  type SpotlightSpeaker,
} from "./types"

export interface SpotlightTenantInfo {
  name: string
  logo_url?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  website?: string | null
}

const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif"

// Colour-less structure: hairlines, labels and the soft panels. Fixed on
// purpose — the palette recolours the brand parts, never the scaffolding.
export const NEUTRAL_LINE = "#e5e7eb"
export const NEUTRAL_LABEL = "#8b919c"
export const NEUTRAL_PANEL = "#f7f8fa"

// The header logo is drawn at an EXPLICIT pixel size: Outlook's Word engine
// ignores `width:auto` and would paint the 924px source at full size. 140×50
// is the regular logo's aspect ratio (924×330); an uploaded logo is fitted
// into the same box, so upload a wide one.
const LOGO_W = 140
const LOGO_H = 50

// Must match crm-api `CampaignSanitizerHelper::MSO_GHOST_OPEN/CLOSE` EXACTLY —
// any other conditional comment is stripped before the email is sent.
export const MSO_GHOST_OPEN =
  '<!--[if mso]><table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" width="600"><tr><td><![endif]-->'
export const MSO_GHOST_CLOSE = "<!--[if mso]></td></tr></table><![endif]-->"

// HTML-escape a plain-text value. The rich-text intro is intentionally NOT
// escaped (TipTap HTML, re-sanitized server-side by crm-api's allowlist).
const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string))

// Escape, then turn newlines into <br/> — for speaker titles, eyebrow, when.
const escLines = (s: unknown): string =>
  String(s ?? "")
    .split(/\r?\n/)
    .map((line) => esc(line.trim()))
    .filter(Boolean)
    .join("<br/>")

// Normalize a user-entered link to an absolute http(s) href. Any other scheme
// (javascript:, data:, …) or a blank value collapses to "#". The buttons ALWAYS
// render; a blank link is blocked at send time (`send-validation.ts`).
export const safeHref = (url: string): string => {
  const v = (url || "").trim()
  if (!v) return "#"
  if (/^https?:\/\//i.test(v)) return v
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "#"
  return `https://${v}`
}

// Colours land inside `style="…"` attributes, so ONLY a plain #rgb / #rrggbb
// value is accepted — anything else (a `;` smuggling another declaration, a
// `"` closing the attribute, a named colour typo) falls back to the default.
export const safeColor = (value: unknown, fallback: string): string => {
  const v = String(value ?? "").trim()
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) ? v : fallback
}

export function resolveColors(colors: Partial<SpotlightColors> | undefined): SpotlightColors {
  const d = DEFAULT_SPOTLIGHT_COLORS
  const c = colors ?? {}
  return {
    accent: safeColor(c.accent, d.accent),
    button: safeColor(c.button, d.button),
    buttonText: safeColor(c.buttonText, d.buttonText),
    heading: safeColor(c.heading, d.heading),
    text: safeColor(c.text, d.text),
    pageBackground: safeColor(c.pageBackground, d.pageBackground),
    cardBackground: safeColor(c.cardBackground, d.cardBackground),
  }
}

// A 1px neutral hairline row spanning the card's content width.
const hairline = (pad = "0 48px") =>
  `<tr><td style="padding:${pad};"><table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation"><tr><td style="border-top:1px solid ${NEUTRAL_LINE}; font-size:1px; line-height:1px;">&#160;</td></tr></table></td></tr>`

// Small upper-case, letter-spaced neutral label.
const label = (text: string, extra = "") =>
  `<p style="margin:0; color:${NEUTRAL_LABEL}; font-size:11px; font-weight:bold; letter-spacing:2px; line-height:16px; font-family:${FONT};${extra}">${esc(text.toUpperCase())}</p>`

// Padding-on-the-cell button (survives Outlook's Word engine without VML).
function buttonHtml(text: string, href: string, colors: SpotlightColors): string {
  const safe = safeHref(href)
  const t = esc(text || "Register Now")
  return `<table cellpadding="0" cellspacing="0" border="0" align="center" role="presentation" style="margin:0 auto;">
                <tr>
                  <td align="center" bgcolor="${colors.button}" style="background-color:${colors.button}; border-radius:8px; padding:15px 38px;">
                    <a href="${safe}" target="_blank" style="color:${colors.buttonText}; font-size:16px; font-weight:bold; letter-spacing:0.3px; line-height:20px; text-decoration:none; font-family:${FONT}; display:inline-block;">${t}&#160;&#8594;</a>
                  </td>
                </tr>
              </table>`
}

// Initials for a speaker with no headshot ("Jim Lundy" → "JL").
export const initials = (name: string): string =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("") || "?"

function speakerCellHtml(s: SpotlightSpeaker, colors: SpotlightColors, widthPct: number, size: number): string {
  const r = size / 2
  const photo = (s.photoUrl || "").trim()
  const avatar = photo
    ? `<img src="${toAbsoluteUrl(photo)}" alt="${esc(s.name)}" width="${size}" height="${size}" style="width:${size}px; height:${size}px; border-radius:${r}px; display:block; margin:0 auto; border:0;" />`
    : `<table cellpadding="0" cellspacing="0" border="0" align="center" role="presentation" style="margin:0 auto;"><tr><td align="center" valign="middle" width="${size}" height="${size}" bgcolor="${colors.accent}" style="width:${size}px; height:${size}px; border-radius:${r}px; background-color:${colors.accent}; color:#ffffff; font-size:${Math.round(size * 0.3)}px; font-weight:bold; font-family:${FONT}; text-align:center;">${esc(initials(s.name))}</td></tr></table>`
  return `
                  <td width="${widthPct}%" valign="top" align="center" style="width:${widthPct}%; padding:0 6px; text-align:center; vertical-align:top;">
                    ${avatar}
                    <p style="margin:14px 0 2px 0; color:${colors.heading}; font-size:16px; font-weight:bold; line-height:22px; font-family:${FONT};">${esc(s.name)}</p>
                    <p style="margin:0; color:${NEUTRAL_LABEL}; font-size:13px; line-height:19px; font-family:${FONT};">${escLines(s.title)}</p>
                  </td>`
}

function topicRowsHtml(items: string[], colors: SpotlightColors): string {
  return items
    .map(
      (item, i) => `
                <tr>
                  <td width="44" valign="top" style="width:44px; padding:14px 0; border-top:1px solid ${NEUTRAL_LINE}; color:${NEUTRAL_LABEL}; font-size:13px; font-weight:bold; letter-spacing:1px; line-height:24px; font-family:${FONT};">${String(i + 1).padStart(2, "0")}</td>
                  <td valign="top" style="padding:14px 0; border-top:1px solid ${NEUTRAL_LINE}; color:${colors.text}; font-size:16px; line-height:24px; font-family:${FONT};">${esc(item)}</td>
                </tr>`,
    )
    .join("")
}

function footerHtml(tenant: SpotlightTenantInfo | undefined, colors: SpotlightColors, year: number): string {
  const company = tenant?.name || "XOP Networks"
  const address = [tenant?.address, tenant?.city, tenant?.state, tenant?.postal_code, tenant?.country]
    .map((s) => (s ? String(s).trim() : ""))
    .filter(Boolean)
    .join(", ")
  return `
          <tr>
            <td bgcolor="${NEUTRAL_PANEL}" style="background-color:${NEUTRAL_PANEL}; border-top:1px solid ${NEUTRAL_LINE}; border-radius:0 0 12px 12px; padding:36px 48px 32px 48px; text-align:center;" align="center">
              ${socialFooterLinksHtml({ theme: "light", marginStyle: "margin:0 0 24px 0;" })}
              <p style="margin:0 0 6px 0; color:${NEUTRAL_LABEL}; font-size:12px; line-height:18px; font-family:${FONT};">${esc(company)}${address ? ` &#183; ${esc(address)}` : ""}</p>
              ${tenant?.website ? `<p style="margin:0 0 6px 0; font-size:12px; line-height:18px; font-family:${FONT};"><a href="${safeHref(tenant.website)}" target="_blank" style="color:${colors.accent}; text-decoration:none; font-weight:bold;">${esc(tenant.website)}</a></p>` : ""}
              <p style="margin:0; color:${NEUTRAL_LABEL}; font-size:11px; line-height:16px; font-family:${FONT};">&#169; ${year} ${esc(company)}. All rights reserved.</p>
            </td>
          </tr>`
}

export function generateSpotlightHtml(
  section: SpotlightSection | undefined,
  tenant: SpotlightTenantInfo | undefined,
  year: number,
): string {
  const c: Partial<SpotlightContent> = section?.content ?? {}
  const colors = resolveColors(c.colors)
  const company = tenant?.name || "XOP Networks"

  const title = (c.title || "").trim() || "Your Webinar Title"
  const subtitle = (c.subtitle || "").trim()
  const eyebrow = (c.eyebrow || "").trim()
  const preheader = (c.preheader || "").trim()
  // The regular horizontal XOP logo unless an operator uploaded another one.
  // Deliberately NOT the tenant `logo_url` — that is the old stacked mark.
  const logoSrc = (c.logoUrl || "").trim() || XOP_EMAIL_LOGO_URL
  const bannerSrc = (c.bannerUrl || "").trim()
  const greeting = (c.greeting || "").trim()
  const intro = c.intro || ""
  const coverItems = (c.coverItems || []).map((s) => s.trim()).filter(Boolean)
  const closingLine = (c.closingLine || "").trim()
  const when = (c.when || "").trim()
  const speakers = (c.speakers || []).filter((s) => (s.name || "").trim()).slice(0, SPOTLIGHT_MAX_SPEAKERS)
  const button = buttonHtml(c.buttonLabel || "Register Now", c.registrationLink || "", colors)

  const sectionHeading = (text: string) =>
    `<p style="margin:0; color:${colors.heading}; font-size:22px; font-weight:bold; letter-spacing:-0.3px; line-height:28px; font-family:${FONT};">${esc(text)}</p>`

  const speakersBlock = speakers.length
    ? `
          ${hairline()}
          <tr>
            <td style="padding:36px 48px 0 48px; text-align:center;" align="center">
              ${label("Speakers", "margin:0 0 8px 0;")}
              ${sectionHeading(c.speakersHeading || "Meet Our Speakers")}
            </td>
          </tr>
          <tr>
            <td style="padding:28px 24px 40px 24px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>${speakers.map((s) => speakerCellHtml(s, colors, Math.floor(100 / speakers.length), speakers.length > 2 ? 72 : 112)).join("")}
                </tr>
              </table>
            </td>
          </tr>`
    : ""

  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <title>${esc(title)}</title>
</head>
<body style="margin:0; padding:0; font-family:${FONT}; background-color:${colors.pageBackground};">
  ${preheader ? `<div style="display:none; max-height:0; overflow:hidden; font-size:1px; line-height:1px; color:${colors.pageBackground};">${esc(preheader)}</div>` : ""}
  <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="${colors.pageBackground}" style="background-color:${colors.pageBackground};">
    <tr>
      <td align="center" style="padding:32px 12px;">
        ${MSO_GHOST_OPEN}
        <table align="center" width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="${colors.cardBackground}" style="width:100%; max-width:600px; background-color:${colors.cardBackground}; border:1px solid ${NEUTRAL_LINE}; border-radius:12px;">

          <!-- Header: logo left, quiet tag right -->
          <tr>
            <td style="padding:26px 40px 22px 40px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td align="left" valign="middle" style="text-align:left;">
                    <img src="${toAbsoluteUrl(logoSrc)}" alt="${esc(company)}" width="${LOGO_W}" height="${LOGO_H}" style="width:${LOGO_W}px; height:${LOGO_H}px; display:block; border:0;" />
                  </td>
                  <td align="right" valign="middle" style="text-align:right;">${label("Live webinar")}</td>
                </tr>
              </table>
            </td>
          </tr>

          ${
            bannerSrc
              ? `<!-- Banner -->
          <tr>
            <td style="padding:0; line-height:0; font-size:0;">
              <img src="${toAbsoluteUrl(bannerSrc)}" alt="${esc(title)}" width="600" style="width:100%; max-width:600px; height:auto; display:block; border:0;" />
            </td>
          </tr>`
              : hairline("0")
          }

          <!-- Eyebrow + title -->
          <tr>
            <td style="padding:44px 48px 0 48px; text-align:center;" align="center">
              ${eyebrow ? `<p style="margin:0 0 18px 0; color:${colors.accent}; font-size:12px; font-weight:bold; letter-spacing:1.5px; line-height:18px; font-family:${FONT};">${escLines(eyebrow.toUpperCase())}</p>` : ""}
              <h1 style="margin:0; color:${colors.heading}; font-size:32px; font-weight:bold; letter-spacing:-0.6px; line-height:38px; font-family:${FONT};">${esc(title)}</h1>
              ${subtitle ? `<p style="margin:6px 0 0 0; color:${colors.accent}; font-size:24px; font-style:italic; font-weight:bold; letter-spacing:-0.3px; line-height:31px; font-family:${FONT};">${esc(subtitle)}</p>` : ""}
              <table cellpadding="0" cellspacing="0" border="0" align="center" role="presentation" style="margin:26px auto 0 auto;"><tr><td width="56" style="width:56px; border-top:2px solid ${NEUTRAL_LINE}; font-size:1px; line-height:1px;">&#160;</td></tr></table>
            </td>
          </tr>

          <!-- Register (top) -->
          <tr>
            <td style="padding:26px 48px 40px 48px; text-align:center;" align="center">
              ${button}
            </td>
          </tr>

          ${hairline()}

          <!-- Greeting + intro -->
          <tr>
            <td style="padding:36px 48px 0 48px; color:${colors.text}; font-size:16px; line-height:26px; font-family:${FONT};">
              ${greeting ? `<p style="margin:0 0 16px 0; color:${colors.heading}; font-size:17px; font-weight:bold;">${esc(greeting)}</p>` : ""}
              ${intro}
            </td>
          </tr>

          ${
            coverItems.length
              ? `<!-- What we'll cover -->
          <tr>
            <td style="padding:28px 48px 0 48px;">
              ${label(c.coverHeading || "During the webinar, we'll cover:", "margin:0 0 10px 0;")}
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">${topicRowsHtml(coverItems, colors)}
                <tr><td colspan="2" style="border-top:1px solid ${NEUTRAL_LINE}; font-size:1px; line-height:1px;">&#160;</td></tr>
              </table>
            </td>
          </tr>`
              : ""
          }

          ${
            closingLine
              ? `<tr>
            <td style="padding:22px 48px 0 48px; color:${colors.heading}; font-size:17px; line-height:26px; font-weight:bold; font-style:italic; font-family:${FONT};">${esc(closingLine)}</td>
          </tr>`
              : ""
          }

          ${
            when
              ? `<!-- When -->
          <tr>
            <td style="padding:36px 48px 0 48px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td bgcolor="${NEUTRAL_PANEL}" align="center" style="background-color:${NEUTRAL_PANEL}; border:1px solid ${NEUTRAL_LINE}; border-radius:10px; padding:22px 20px; text-align:center;">
                    ${label(c.whenHeading || "When?", "margin:0 0 8px 0;")}
                    <p style="margin:0; color:${colors.heading}; font-size:18px; font-weight:bold; line-height:26px; font-family:${FONT};">${escLines(when)}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`
              : ""
          }

          <!-- Register (bottom) -->
          <tr>
            <td style="padding:32px 48px 44px 48px; text-align:center;" align="center">
              ${button}
            </td>
          </tr>

          ${speakersBlock}

          ${footerHtml(tenant, colors, year)}

        </table>
        ${MSO_GHOST_CLOSE}
      </td>
    </tr>
  </table>
</body>
</html>`
}
